import { useEffect, useRef, useState } from "react";
import { useWarMusic, type WarTrack } from "../hooks/useWarMusic";
import { useTheme } from "../lib/theme/ThemeProvider";

/**
 * Ambient music player — ONE persistent instance, mounted at the app root
 * (App.tsx, above <Routes>) so playback continues across page navigation
 * for the whole session. Theme-aware playlist, autostart, floating widget.
 *
 *   norse    → war_music bucket ROOT
 *   frontier → war_music/frontier/   (drop western mp3s here)
 *
 * Autostart: tries to play on load; browsers block autoplay-with-sound until
 * a user gesture, so we arm a one-time global listener that starts playback on
 * the first click / key / touch anywhere. Preference persists (default ON).
 */
const MUSIC_FOLDER: Record<string, string> = { norse: "", frontier: "frontier" };

function WarRoomMusic() {
  const { themeId } = useTheme();
  const folder = MUSIC_FOLDER[themeId] ?? "";
  const { data: tracks = [], isLoading } = useWarMusic(folder);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [open, setOpen] = useState(false);
  const [trackIndex, setTrackIndex] = useState(0);
  const [playing, setPlaying] = useState<boolean>(() => {
    const v = localStorage.getItem("ld-music-on");
    return v === null ? true : v === "1"; // default ON
  });
  const [volume, setVolume] = useState<number>(() => {
    const v = localStorage.getItem("war-music-volume");
    return v ? Number(v) : 0.4;
  });

  // Persist preferences + apply volume to element.
  useEffect(() => {
    localStorage.setItem("ld-music-on", playing ? "1" : "0");
  }, [playing]);
  useEffect(() => {
    localStorage.setItem("war-music-volume", String(volume));
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Theme switch → different playlist → restart from the top.
  useEffect(() => {
    setTrackIndex(0);
  }, [folder]);

  // Drive the element + AUTOSTART. When playback is wanted and tracks exist,
  // try to play; if the browser blocks it, arm a one-time gesture listener.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (!playing || tracks.length === 0) {
      el.pause();
      return;
    }
    let removeGesture = () => {};
    el.play().catch(() => {
      const onGesture = () => {
        el.play().catch(() => {});
        removeGesture();
      };
      window.addEventListener("pointerdown", onGesture);
      window.addEventListener("keydown", onGesture);
      window.addEventListener("touchstart", onGesture);
      removeGesture = () => {
        window.removeEventListener("pointerdown", onGesture);
        window.removeEventListener("keydown", onGesture);
        window.removeEventListener("touchstart", onGesture);
      };
    });
    return () => removeGesture();
  }, [playing, trackIndex, tracks.length, folder]);

  function onEnded() {
    if (tracks.length === 0) return;
    setTrackIndex((i) => (i + 1) % tracks.length);
    setPlaying(true);
  }
  function togglePlay() {
    if (tracks.length === 0) return;
    setPlaying((p) => !p);
  }
  function next() {
    if (tracks.length === 0) return;
    setTrackIndex((i) => (i + 1) % tracks.length);
  }
  function prev() {
    if (tracks.length === 0) return;
    setTrackIndex((i) => (i - 1 + tracks.length) % tracks.length);
  }

  const currentTrack: WarTrack | undefined = tracks[trackIndex];
  const hasContent = tracks.length > 0;
  const label = themeId === "frontier" ? "// trail music" : "// war-room music";
  const bucketHint = themeId === "frontier" ? "war_music/frontier" : "war_music";

  return (
    <div className="fixed bottom-4 right-4 z-50 select-none">
      <audio ref={audioRef} src={currentTrack?.url} onEnded={onEnded} preload="metadata" />

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="open music player"
          className={`group flex items-center gap-2 rounded-full border border-steel-dark bg-iron/90 px-4 py-2 backdrop-blur-sm transition-all hover:border-ember-bright/60 hover:bg-iron-2 ${playing ? "shadow-ember-glow" : ""}`}
        >
          <NoteIcon active={playing} />
          <span className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute group-hover:text-bone">
            {playing && currentTrack ? "playing" : "music"}
          </span>
        </button>
      ) : (
        <ExpandedPlayer
          tracks={tracks}
          isLoading={isLoading}
          currentTrack={currentTrack}
          trackIndex={trackIndex}
          playing={playing}
          volume={volume}
          label={label}
          bucketHint={bucketHint}
          onClose={() => setOpen(false)}
          onTogglePlay={togglePlay}
          onPrev={prev}
          onNext={next}
          onPick={(i) => {
            setTrackIndex(i);
            setPlaying(true);
          }}
          onVolume={setVolume}
          hasContent={hasContent}
        />
      )}
    </div>
  );
}

function ExpandedPlayer({
  tracks,
  isLoading,
  currentTrack,
  trackIndex,
  playing,
  volume,
  label,
  bucketHint,
  onClose,
  onTogglePlay,
  onPrev,
  onNext,
  onPick,
  onVolume,
  hasContent,
}: {
  tracks: WarTrack[];
  isLoading: boolean;
  currentTrack: WarTrack | undefined;
  trackIndex: number;
  playing: boolean;
  volume: number;
  label: string;
  bucketHint: string;
  onClose: () => void;
  onTogglePlay: () => void;
  onPrev: () => void;
  onNext: () => void;
  onPick: (i: number) => void;
  onVolume: (v: number) => void;
  hasContent: boolean;
}) {
  return (
    <div className="w-80 rounded border border-steel-dark bg-iron/95 shadow-2xl backdrop-blur-md">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-4 py-2 font-display text-base font-light uppercase tracking-[0.14em]">
        <span className="text-bone-dim">{label}</span>
        <button type="button" onClick={onClose} aria-label="close" className="text-bone-mute hover:text-bone">
          ✕
        </button>
      </div>

      <div className="px-4 py-3">
        {isLoading ? (
          <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">// loading the archive</p>
        ) : !hasContent ? (
          <p className="font-display text-sm text-bone-dim">
            No tracks yet. Drop MP3s into the{" "}
            <code className="font-display text-bone">{bucketHint}</code> bucket on berserkr-app and they'll appear here.
          </p>
        ) : (
          <>
            <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">now playing</p>
            <p className="mt-0.5 truncate font-display text-base font-light text-bone">
              {currentTrack?.displayName ?? "—"}
            </p>

            <div className="mt-3 flex items-center gap-2">
              <ControlButton onClick={onPrev} label="prev">◀</ControlButton>
              <ControlButton onClick={onTogglePlay} label={playing ? "pause" : "play"} primary>
                {playing ? "❚❚" : "▶"}
              </ControlButton>
              <ControlButton onClick={onNext} label="next">▶</ControlButton>

              <div className="flex flex-1 items-center gap-2 pl-3">
                <span className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">vol</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={(e) => onVolume(Number(e.target.value))}
                  className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-steel-dark accent-ember-bright"
                />
              </div>
            </div>

            <div className="mt-4 max-h-40 overflow-y-auto rounded border border-steel-dark/70 bg-charcoal/40">
              <ul>
                {tracks.map((t, i) => (
                  <li key={t.filename}>
                    <button
                      type="button"
                      onClick={() => onPick(i)}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left font-display text-base font-light uppercase tracking-[0.12em] transition-colors ${
                        i === trackIndex
                          ? "bg-ember/20 text-ember-bright"
                          : "text-bone-dim hover:bg-iron-2/60 hover:text-bone"
                      }`}
                    >
                      <span className="truncate">
                        {String(i + 1).padStart(2, "0")} // {t.displayName}
                      </span>
                      {i === trackIndex && playing ? (
                        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright" />
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ControlButton({
  children,
  onClick,
  label,
  primary,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`flex h-8 w-8 items-center justify-center rounded border font-display text-sm transition-colors ${
        primary
          ? "border-ember-bright/60 bg-ember/30 text-bone hover:bg-ember/50 hover:shadow-ember-soft"
          : "border-steel-dark bg-iron/60 text-bone-dim hover:border-ember-bright/40 hover:text-bone"
      }`}
    >
      {children}
    </button>
  );
}

function NoteIcon({ active }: { active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className={`h-4 w-4 ${active ? "text-ember-bright" : "text-bone-mute"}`} aria-hidden>
      <path
        fill="currentColor"
        d="M9 17a3 3 0 1 1-2-2.83V6l11-2v8.17A3 3 0 1 1 16 12V7l-7 1.27V17z"
      />
      {active ? (
        <circle cx="20" cy="5" r="1.5" fill="currentColor" className="animate-pulse-ember" />
      ) : null}
    </svg>
  );
}

export default WarRoomMusic;
